import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const NONE = "__none";

export const Field = ({ label, hint, error, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs font-medium text-gray-700">{label}{hint && <span className="text-gray-400 font-normal ml-1">{hint}</span>}</Label>
    {children}
    {error && <p className="text-xs text-red-600" data-testid="field-error">{error}</p>}
  </div>
);

export const SelectField = ({ value, onChange, options, placeholder = "Select", testid, allowNone = true }) => (
  <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? "" : v)}>
    <SelectTrigger data-testid={testid} className="h-9"><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      {allowNone && <SelectItem value={NONE}><span className="text-gray-400">{placeholder}</span></SelectItem>}
      {options.map((o) => {
        const [v, l] = Array.isArray(o) ? o : [o, o];
        return <SelectItem key={v} value={v}>{l}</SelectItem>;
      })}
    </SelectContent>
  </Select>
);

const FILLING = ["Valve packer", "Open-mouth auto filler", "Manual filling", "FFS (form-fill-seal)", "Not sure yet"];
const BAG_TYPES = ["Woven PP bag", "BOPP laminated woven bag", "Valve bag", "Block-bottom valve bag", "Woven PP bag with liner", "FIBC jumbo bag", "Not sure yet"];

export const CORE_KEYS = ["product", "fill_weight_kg", "monthly_bags", "travel_distance_km", "liner", "drops_per_bag"];

export function RequirementsFields({ value, onChange, compact = false }) {
  const v = value || {};
  const set = (k) => (e) => onChange({ ...v, [k]: e?.target ? e.target.value : e });
  const num = (k, label, hint, ph) => (
    <Field label={label} hint={hint}>
      <Input data-testid={`req-${k}`} type="number" min="0" inputMode="decimal" placeholder={ph} value={v[k] ?? ""} onChange={set(k)} className="h-9" />
    </Field>
  );
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Product / material to be filled" className="sm:col-span-2">
          <Input data-testid="req-product" placeholder="e.g. Tile adhesive powder" value={v.product ?? ""} onChange={set("product")} className="h-9" />
        </Field>
        {num("fill_weight_kg", "Filling weight per bag", "(kg)", "25")}
        {num("monthly_bags", "Estimated monthly requirement", "(bags)", "40000")}
        {num("travel_distance_km", "Approx. travel distance", "(km)", "500")}
        {num("drops_per_bag", "Expected drops per filled bag", "", "5")}
        <Field label="Liner requirement">
          <SelectField testid="req-liner" value={v.liner} onChange={set("liner")} options={["Yes", "No", "Unsure"]} placeholder="Not confirmed" />
        </Field>
        <Field label="Preferred bag type">
          <SelectField testid="req-bag_type" value={v.bag_type} onChange={set("bag_type")} options={BAG_TYPES} placeholder="Not confirmed" />
        </Field>
      </div>
      {!compact && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-5 border-t border-gray-100">
          <Field label="Filling method">
            <SelectField testid="req-filling_method" value={v.filling_method} onChange={set("filling_method")} options={FILLING} placeholder="Not confirmed" />
          </Field>
          <Field label="Bag dimensions" hint="(W x L x gusset)">
            <Input data-testid="req-bag_dimensions" placeholder="e.g. 50 x 80 cm" value={v.bag_dimensions ?? ""} onChange={set("bag_dimensions")} className="h-9" />
          </Field>
          <Field label="Printing requirements" className="sm:col-span-2">
            <Input data-testid="req-printing" placeholder="e.g. 4-colour BOPP, both sides" value={v.printing ?? ""} onChange={set("printing")} className="h-9" />
          </Field>
          <Field label="Existing packaging problems">
            <Textarea data-testid="req-existing_problems" rows={3} value={v.existing_problems ?? ""} onChange={set("existing_problems")} placeholder="Bursting, leakage, moisture…" />
          </Field>
          <Field label="Current packaging specifications">
            <Textarea data-testid="req-current_specs" rows={3} value={v.current_specs ?? ""} onChange={set("current_specs")} placeholder="GSM, mesh, denier, lamination…" />
          </Field>
        </div>
      )}
      <Field label="Additional technical requirements">
        <Textarea data-testid="req-additional_requirements" rows={compact ? 2 : 3} value={v.additional_requirements ?? ""} onChange={set("additional_requirements")} placeholder="Anything else — leave blank if not confirmed yet" />
      </Field>
    </div>
  );
}

export const cleanReqs = (r = {}) => {
  const out = {};
  Object.entries(r).forEach(([k, v]) => {
    if (v === "" || v === undefined || v === null) return;
    out[k] = ["fill_weight_kg", "travel_distance_km"].includes(k) ? parseFloat(v)
      : ["monthly_bags", "drops_per_bag"].includes(k) ? parseInt(v, 10) : v;
  });
  return out;
};
